import type { HTMLAttributes } from 'react';
import { merge, get } from 'lodash';
import classNames from 'classnames';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import deferHydration from '$util/deferHydration';
import type { AnyObject } from '$util/types';
import type { CmsContentDictionary } from '$cms/index';
import { getShortTextByKey } from '$cms/getDataByKey';
import MainHead from '$layout/partials/MainHead';
import type { CmsPageSeo } from '$cms/types';
import { PageTopMegaNavigationBar } from '$quantum/templates/PageTopMegaNavigationBar';
import type { SeoStructuredData } from '@/util/seoStructuredData';
import { TopNavBannerClient } from '@/components/molecules/TopNavBanner/TopNavBannerClient';
import { QueuedModals } from '@/components/organisms/QueuedModals';

const Footer = deferHydration(() => import('$components/layout/Footer'), {
  hydrationId: '$components/layout/Footer',
  trigger: 'view',
});

const getCmsData = (cmsData: CmsContentDictionary) => {
  const getShortText = getShortTextByKey.bind(cmsData);

  return {
    ogFacebookUrl: getShortText('homepage.openGraph.facebook.url', 'https://www.facebook.com/hellofixter'),
    ogTwitterSite: getShortText('homepage.openGraph.twitter.site', '@hellofixter'),
    ogTwitterCreator: getShortText('homepage.openGraph.twitter.creator', '@fdermer'),
    domainVerificationFacebook: getShortText('domainVerification.facebook', 'vcy7n7tpj2m5c020wzch1vg3dl7hjk'),
  };
};

interface MainLayoutAppContext extends AppContextGlobal {
  readonly pageMeta?: AnyObject;
  readonly pageTypeFromPath?: string;
}

/**
 * @todo delete deprecated fields after they are removed from all pages.
 */
export interface MainLayoutProps extends HTMLAttributes<HTMLElement> {
  /**
   * Values read directly from CMS.
   * @deprecated
   */
  readonly pageMeta?: AnyObject;
  /**
   * Values read directly from CMS.
   * @deprecated
   */
  readonly openGraph?: AnyObject;
  /**
   * Values read directly from CMS.
   * @deprecated
   */
  readonly domainVerification?: AnyObject;
  /**
   * Page title and meta description need to come from outside of the components or else we risk displaying the same title and meta description on all pages that use the <MainLayout> component.
   * Bug reference: https://fixter.atlassian.net/browse/BUG-931
   */
  readonly cmsPageMeta?: AnyObject;

  readonly seo?: CmsPageSeo;

  readonly seoStructuredData?: SeoStructuredData;

  readonly showFooter?: boolean;

  readonly showBanners?: boolean;
}

export default function MainLayout({
  pageMeta: deprecatedPageMeta,
  openGraph: deprecatedOpenGraph,
  domainVerification: deprecatedDomainVerification,
  cmsPageMeta,
  seo,
  seoStructuredData,
  children,
  className,
  showFooter = true,
  showBanners = true,
}: MainLayoutProps): JSX.Element {
  const {
    pageMeta: serverPageMeta,
    cmsContent: { dictionary },
    locale,
    segment,
    campaign,
    location: { pathname },
  } = useAppContext<MainLayoutAppContext>();

  const { ogFacebookUrl, ogTwitterCreator, ogTwitterSite, domainVerificationFacebook } =
    getCmsData(dictionary);

  const pageMeta = merge(
    {},
    deprecatedPageMeta,
    serverPageMeta,
    cmsPageMeta,
    seoStructuredData && {
      schemaFAQs: seoStructuredData,
    }
  );

  const openGraph = deprecatedOpenGraph ?? {
    facebookUrl: ogFacebookUrl,
    twitterSite: ogTwitterSite,
    twitterCreator: ogTwitterCreator,
  };
  const domainVerification = deprecatedDomainVerification ?? { facebook: domainVerificationFacebook };

  const page = get(deprecatedPageMeta, 'page', '');
  const pageType = get(deprecatedPageMeta, 'typeString', '');

  return (
    <main className={classNames(page, pageType, className)}>
      <MainHead
        seo={seo}
        pageMeta={pageMeta}
        locale={locale}
        openGraph={openGraph}
        domainVerification={domainVerification}
      />
      <PageTopMegaNavigationBar campaignId={campaign?.campaignId} />
      {showBanners && campaign && (
        <TopNavBannerClient
          type="primary"
          icon="finance/tag-01-flipped"
          segment={segment}
          source={campaign.source}
        >
          {campaign?.name}
        </TopNavBannerClient>
      )}
      {pathname.includes('mot-and-service') && (
        <TopNavBannerClient icon="finance/tag-01-flipped" type="secondary" segment={segment}>
          50% off your MOT with a Full Service
        </TopNavBannerClient>
      )}
      {children}
      {showFooter && <Footer />}
      <QueuedModals />
    </main>
  );
}
